import type { TowerDesign } from "@/game/towerSpec";

/**
 * 30 m 3-legged (triangular) self-supporting lattice — the classic ROHN SSV
 * form (tubular legs, angle bracing, three faces). Owned by the lattice agent.
 *
 * A triangular tower uses ~25% less steel than a 4-legged one of the same height
 * (three legs and faces instead of four) and is a genuinely different structural
 * showcase: with only three legs, the overturning couple is shared across fewer
 * chords, so leg forces per unit wind are higher — the base has to work harder.
 *
 * Geometry / rationale:
 *  - Base width 4.2 m at 30 m tall → base/height ≈ 1/7.1, inside the real
 *    1/6–1/12 range for self-supporting towers (docs/ENGINEERING.md §3.2).
 *  - 10 panels → 3.0 m panel height. Legs: 3" Sch-40 pipe (legMed, I = 3.02 in⁴)
 *    buckle at Pcr ≈ 276 kN — buckling governs here (below the 497 kN yield cap),
 *    the slender-column drama the game wants. Comfortably above the ~115 kN
 *    windward-leg force at the 40 m/s design gust.
 *  - Bracing: L3×3×1/4 angles (braceMed).
 *  - Heavy taper (baseR 2.1 → topR 0.7), classic SSV silhouette.
 *  - Peak leg utilization: ~4% dead-only, ~42% at the 40 m/s TIA design gust,
 *    ~76% near 55 m/s, failing (windward leg buckles → topple) past ~63 m/s.
 */
export const design: TowerDesign = {
  id: "lattice-tri-30m",
  name: "Triangular Lattice",
  description: "30 m 3-legged self-supporting SSV-style tower (base/height ≈ 1/7).",
  spec: {
    type: "lattice",
    height: 30,
    stack: {
      faces: 3, // triangular — three tubular legs, ROHN SSV form
      panels: 10, // 3.0 m panels
      baseRadius: 2.1, // base width 4.2 m ≈ height/7.1
      topRadius: 0.7,
      legSection: "legMed", // 3" Sch-40 pipe — Pcr ≈ 276 kN (buckling governs)
      braceSection: "braceMed", // L3×3×1/4 angle bracing
      legMaterial: "a572", // A572 Gr50 (Fy 345 MPa)
      bracing: "X",
    },
  },
};
